import * as R from 'ramda';
import { Form, message } from 'antd';
import { connect } from 'react-redux';
import {
  branch,
  lifecycle,
  withProps,
  withHandlers,
  renderComponent,
  withStateHandlers,
} from 'recompose';

import { debounceTime } from '../constants';
import { fetchRequests } from '../../api/apiko-api';
import { fetchUsers } from '../../redux/thunk/users';
import { Spinner } from '../../components/ui-helpers';
import { TasksModal as Component } from './component';
import { openModal, closeCurrentModal } from '../modal-reducer';
import { handleTaskCreate, handleTaskUpdate } from '../../redux/thunk/tasks';
import {
  debounce,
  filterUsers,
  getItemById,
  getItemsById,
  getItemByPath,
  checkRequestForNextAction,
} from '../../utils';


const debouncedFetch = debounce(fetchRequests, debounceTime);

const getModalTitle = ({ payload, task }) => {
  if (payload.isWithoutRequest && !task._id) {
    return 'Add Task...';
  }
  return task._id ? 'Update Task' : 'Add Task';
};

const getTask = (state, { requestId = '', taskId, isWithoutRequest }) => {
  const {
    requests = {}, contacts = {}, tasks = {}, users = {}, organizationSettings = {},
  } = state;

  const request = getItemById(requestId, requests);
  const requestContacts = getItemsById(request.contacts, contacts);
  const requestAssignees = request.assignees
    ? request.assignees.map(assignee => getItemByPath(['items', assignee], users))
    : null;

  const requestSource = getItemByPath(
    ['items', 'requestSources', request.source],
    organizationSettings,
  );

  // Create a new Task...
  if (!taskId) {
    return {
      request: {
        ...request,
        ...(isWithoutRequest ? {} : { assignees: requestAssignees }),
        contacts: requestContacts,
        source: requestSource,
      },
    };
  }

  const task = getItemById(taskId, tasks);
  const assignedTo = getItemByPath(['items', task.assignedTo], users);

  return {
    ...task,
    assignedTo,
    request: {
      ...request,
      ...(isWithoutRequest ? {} : { assignees: requestAssignees }),
      contacts: requestContacts,
      source: requestSource,
    },
  };
};

const mapStateToProps = (state, { payload }) => {
  const { tasks = {}, users = {} } = state;
  return {
    isLoading: tasks.isCreating || tasks.isUpdating || false,
    task: getTask(state, payload),
    type: payload.type,
    users: filterUsers(users.items),
    isWithoutRequest: payload && payload.isWithoutRequest,
  };
};

export const TasksModal = Form.create()(R.compose(
  connect(mapStateToProps, {
    openModal,
    fetchUsers,
    closeCurrentModal,
    handleTaskCreate,
    handleTaskUpdate,
  }),

  withProps(({ task, payload }) => ({
    modalTitle: getModalTitle({ task, payload }),
  })),

  withStateHandlers(
    ({
      task: {
        _id, title = '',
        description = '',
        taskPriority = 'NO_PRIORITY',
        status = false,
        dueDate,
        dueTime,
        assignedTo,
        request,
      },
      users,
      initialRequest = [],
      initialSelectedRequest = [],
    }) => ({
      _id,
      title,
      description,
      taskPriority,
      status,
      dueDate,
      dueTime,
      users,
      isUpdating: !!_id,
      isShowContacts: false,
      isFetchingRequests: false,
      searchedRequests: initialRequest,
      selectedRequest: initialSelectedRequest,
      userImage: R.path(['userImage'], request),
      selectedUser: R.pathOr(undefined, ['_id'], assignedTo),
    }),
    {
      onToggleShowContacts: ({ isShowContacts }) => () => ({ isShowContacts: !isShowContacts }),
      setSelectedUser: () => value => ({
        selectedUser: value,
      }),
      setIsFetchingRequests: () => value => ({
        isFetchingRequests: value,
      }),
      setSelectedRequest: () => value => ({
        selectedRequest: value,
      }),
      setRequestsData: () => (requests = []) => ({
        searchedRequests: requests,
      }),
    },
  ),

  withHandlers({
    handleRequestSearch: props => (value) => {
      if (value.length > 1) {
        props.setIsFetchingRequests(true);
        debouncedFetch({ searchTerm: value, simpleList: true })
          .then(({ data }) => {
            props.setRequestsData(data.requests);
            props.setIsFetchingRequests(false);
          });
      } else props.setRequestsData();
    },
    onClose: props => () => props.closeCurrentModal(),
    onSave: ({
      form, date, time, status, task, isUpdating, selectedUser, ...actions
    }) => (event) => {
      event.preventDefault();
      form.validateFieldsAndScroll((err, values) => {
        if (!err) {
          const data = {
            status,
            assignedTo: selectedUser,
            dueDate: values.dueDate.toString(),
            dueTime: values.dueTime ? values.dueTime.toString() : '',
            taskPriority: values.taskPriority,
          };
          // leave requestId field, only if it is not empty
          if (!R.isEmpty(values.requestId)) {
            Object.assign(data, values);
          } else Object.assign(data, R.omit(['requestId'], values));

          const requestId = R.pathOr('', ['request', '_id'], task);

          if (requestId) {
            Object.assign(data, { requestId, assignedTo: selectedUser });
          } else if (selectedUser) {
            Object.assign(data, { assignedTo: selectedUser });
          }

          const res = isUpdating
            ? actions.handleTaskUpdate(task._id, data)
            : actions.handleTaskCreate(data);

          res
            .then((updatedTask) => {
              actions.closeCurrentModal();
              checkRequestForNextAction(updatedTask, actions.openModal);
              message.success(`Task successfully ${isUpdating ? 'updated' : 'added'}.`);
            });
        } else {
          console.error(err);
        }
      });
    },
  }),

  lifecycle({
    componentDidMount() {
      if (!this.props.users) {
        this.props.fetchUsers();
      }
    },
  }),
  branch(
    ({ isFetching, users }) => isFetching && users.length === 0,
    renderComponent(Spinner),
  ),
)(Component));



// WEBPACK FOOTER //
// ./src/modals/tasks-modal/container.js